import { isProblemDetails } from "@steelyard/theme";

/**
 * The tone -> class lookup the template's own dashboard keeps in its data array
 * (`app/shared/ecommerce/dashboard/stat-cards.tsx` passes `style: 'text-[#3872FA]'` per card).
 * `MetricCard` takes the classes; deciding which tone applies is this screen's business. Shared by
 * every file in `components/dashboard/` so one tile's colours can't drift from another's.
 */
export const TONE_ICON: Record<string, string> = {
  warning: "bg-orange-lighter text-orange-dark",
  accent: "bg-primary-lighter text-primary-dark",
  success: "bg-green-lighter text-green-dark",
  destructive: "bg-red-lighter text-red-dark",
  muted: "bg-gray-100 text-muted-foreground",
};
export const TONE_METRIC: Record<string, string> = {
  warning: "text-orange-dark",
  accent: "text-primary-dark",
  success: "text-green-dark",
  destructive: "text-red-dark",
  muted: "text-muted-foreground",
};

export type Tone = keyof typeof TONE_ICON;

/** SC-009: severity carried in text too, not colour alone — every tile's metric text says its own state in words ("Unreadable", "0 exceptions"), this just keys the colour to match. */
export function toneIconClass(tone: Tone): string {
  return TONE_ICON[tone];
}
export function toneMetricClass(tone: Tone): string {
  return TONE_METRIC[tone];
}

/**
 * 008-reporting-dashboards T036 (FR-023, constitution VI). One place every tile's `unreadable()`
 * turns a caught `Await` rejection into text: the gateway's own RFC 9457 `detail` (then `title`) if
 * the caught value really is a parsed problem document — an `ApiError` (`lib/api.ts`) always is,
 * since it carries `status`/`title`/`detail`/`raw` as own properties — falling back to the tile's
 * existing hand-written sentence for anything else (a network-level `TypeError`, an aborted fetch).
 * Never a generic "Something went wrong": that would paraphrase the problem document instead of
 * showing it.
 */
export function problemMessage(error: unknown, fallback: string): string {
  if (isProblemDetails(error)) return error.detail ?? error.title ?? fallback;
  return fallback;
}

/**
 * A 4px stripe down the card's leading edge, so a row of Operations cards reads by severity before a
 * single number is. Colour only reinforces — the status pill below says the same thing in words.
 */
export const TONE_STRIPE: Record<Tone, string> = {
  warning: "before:bg-orange",
  accent: "before:bg-primary",
  success: "before:bg-green",
  destructive: "before:bg-red",
  muted: "before:bg-gray-300",
};

/** The status in words (SC-009): what the colour means, never left to the colour alone. */
export const TONE_STATUS: Record<Tone, { label: string; className: string }> = {
  warning: { label: "Review", className: "bg-orange-lighter text-orange-dark" },
  accent: { label: "Info", className: "bg-primary-lighter text-primary-dark" },
  success: { label: "All clear", className: "bg-green-lighter text-green-dark" },
  destructive: { label: "Action needed", className: "bg-red-lighter text-red-dark" },
  muted: { label: "No data", className: "bg-gray-100 text-gray-600" },
};
